import type { EventInfo } from './EventInfo.types';

/**
 * True while the event read has not answered yet — the provider has not populated at all.
 *
 * `loading` alone is not that answer. `useEventInfo` returns a bare `{ loading: false }` until its
 * provider populates, so any hook that gates on the event will briefly report a decision it has not
 * made: the surface commits to its "no" shape for a frame and then reflows. On the hotel search
 * console that showed as the plain tile skeleton flashing before the cash-fund one.
 *
 * What separates the two is the SHAPE, not the values. `EventInfoProvider` always sets the
 * `eventInfo` key on its settled result — `eventInfo: eventInfoData`, undefined and all — while the
 * no-provider fallback and the initial-load branch never set it. Testing the key is what keeps a
 * settled "no event" out of the pending state.
 *
 * That distinction is load-bearing: on an event-id route a deleted event settles with `eventHandle`,
 * `eventInfo` and `error` ALL undefined, so reading their values alone can never tell it apart from
 * the unpopulated fallback, and the caller hangs. `eventHandle` and `error` stay in as additional
 * settle signals so a caller that hands over a partial object still resolves.
 *
 * A plain function of the `useEventInfo()` result rather than a hook of its own, so a caller keeps
 * its single `useEventInfo()` call — and so a spec that mocks that call needs nothing extra.
 *
 * Read it FIRST in an `isLoading`, ahead of every gate that keys on the event.
 */
export const isEventReadPending = (eventInfo: EventInfo): boolean => eventInfo.loading || (!('eventInfo' in eventInfo) && !eventInfo.eventHandle && !eventInfo.error);
